import { useMemo } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import { format, parseISO, isToday } from 'date-fns';
import { Calendar } from 'lucide-react';

interface FlashcardForecastChartProps {
  forecast: { date: string; count: number }[];
}

const FlashcardForecastChart = ({ forecast }: FlashcardForecastChartProps) => {
  const forecastData = useMemo(
    () =>
      forecast.map((f) => ({
        ...f,
        label: format(parseISO(f.date), 'MMM d'),
        isToday: isToday(parseISO(f.date)),
      })),
    [forecast]
  );

  const peak = useMemo(() => Math.max(...forecast.map((f) => f.count), 0), [forecast]);

  return (
    <div className="bg-white/80 backdrop-blur rounded-2xl p-4 sm:p-6 shadow-lg border border-white/60">
      <h3 className="text-sm font-black text-gray-700 uppercase tracking-wide mb-1 flex items-center gap-2">
        <Calendar className="w-4 h-4 text-indigo-500" />
        Forecast — cards due
      </h3>
      <p className="text-xs text-gray-500 mb-4 leading-relaxed">
        How many cards are scheduled for each day over the next 30 days, based on your SM-2{' '}
        <strong className="text-gray-600">due dates</strong>. Today includes overdue and brand-new cards.
        Rating <strong className="text-orange-600">Hard</strong> gives a shorter gap than Good/Easy, so those
        cards show up again sooner on this chart.
      </p>
      <div className="h-56 w-full">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={forecastData} margin={{ top: 8, right: 8, left: -20, bottom: 0 }}>
            <XAxis dataKey="label" tick={{ fontSize: 10 }} interval={4} />
            <YAxis allowDecimals={false} tick={{ fontSize: 10 }} />
            <Tooltip
              contentStyle={{
                borderRadius: 12,
                border: 'none',
                boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
              }}
              formatter={(value: number) => [`${value} cards`, 'Due']}
              labelFormatter={(label) => label}
            />
            <Bar dataKey="count" radius={[6, 6, 0, 0]}>
              {forecastData.map((entry) => (
                <Cell
                  key={entry.date}
                  fill={entry.isToday ? '#4f46e5' : '#a5b4fc'}
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
      {peak > 0 && (
        <p className="text-[10px] text-gray-400 mt-2 text-center">
          Peak day: {peak} cards · bars count scheduled due dates, not difficulty
        </p>
      )}
    </div>
  );
};

export default FlashcardForecastChart;
